import { useState, useMemo } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useShipmentActivities, useAddShipmentNote } from "@/api/shipments";
import { Loader2, Lock } from "lucide-react";
import { format, isToday, isYesterday, isSameDay } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface ActivityTimelineProps {
  shipmentId: string;
  className?: string;
}

interface Activity {
  id: string;
  type: string;
  action: string;
  description: string;
  actorType: string;
  actorId: string | null;
  createdAt: string;
  metadata?: Record<string, unknown>;
}

// Group activities by date
const groupActivitiesByDate = (activities: Activity[]) => {
  const groups: { date: Date; label: string; activities: Activity[] }[] = [];

  for (const activity of activities) {
    const activityDate = new Date(activity.createdAt);
    const existingGroup = groups.find((g) => isSameDay(g.date, activityDate));

    if (existingGroup) {
      existingGroup.activities.push(activity);
    } else {
      let label: string;
      if (isToday(activityDate)) {
        label = "Today";
      } else if (isYesterday(activityDate)) {
        label = "Yesterday";
      } else {
        label = format(activityDate, "MMMM d, yyyy");
      }
      groups.push({ date: activityDate, label, activities: [activity] });
    }
  }

  return groups;
};

export function ActivityTimeline({
  shipmentId,
  className,
}: ActivityTimelineProps) {
  const [noteText, setNoteText] = useState("");

  const { data, isLoading, error } = useShipmentActivities(shipmentId);
  const addNoteMutation = useAddShipmentNote(shipmentId, {
    onSuccess: () => {
      setNoteText("");
      toast.success("Note added");
    },
    onError: () => {
      toast.error("Failed to add note");
    },
  });

  const handleAddNote = () => {
    if (!noteText.trim()) return;
    addNoteMutation.mutate(noteText.trim());
  };

  const activities = (data?.activities ?? []) as Activity[];
  const groupedActivities = useMemo(
    () => groupActivitiesByDate(activities),
    [activities]
  );

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-base font-semibold">Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-base font-semibold">Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Failed to load timeline
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">Timeline</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Comment Input - Always visible like Shopify */}
        <div className="flex gap-3">
          <Avatar className="h-8 w-8 shrink-0">
            <AvatarFallback className="bg-primary/10 text-primary text-xs">
              A
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 space-y-2">
            <Textarea
              placeholder="Leave a comment..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              className="min-h-[60px] resize-none text-sm"
            />
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Lock className="w-3 h-3" />
                <span>Only you and other staff can see comments</span>
              </div>
              <Button
                size="sm"
                onClick={handleAddNote}
                disabled={!noteText.trim() || addNoteMutation.isPending}
              >
                {addNoteMutation.isPending && (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                )}
                Post
              </Button>
            </div>
          </div>
        </div>

        {/* Timeline with date groups */}
        {groupedActivities.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No activity yet
          </p>
        ) : (
          <div className="space-y-4">
            {groupedActivities.map((group) => (
              <div key={group.label}>
                {/* Date label */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs font-medium text-muted-foreground px-2">
                    {group.label}
                  </span>
                  <div className="h-px flex-1 bg-border" />
                </div>

                {/* Activities for this date */}
                <div className="space-y-0">
                  {group.activities.map((activity, index) => {
                    const isLast = index === group.activities.length - 1;

                    return (
                      <div key={activity.id} className="relative pl-5">
                        {/* Vertical line */}
                        {!isLast && (
                          <div className="absolute left-[3px] top-5 w-px h-[calc(100%-12px)] bg-border" />
                        )}

                        {/* Dot - solid, centered with text */}
                        <div
                          className={cn(
                            "absolute left-0 top-[6px] w-[7px] h-[7px] rounded-full",
                            activity.type === "ADMIN_NOTE"
                              ? "bg-primary"
                              : "bg-muted-foreground/50"
                          )}
                        />

                        {/* Content */}
                        <div className="pb-4 flex justify-between gap-4">
                          <p className="text-sm text-foreground">
                            {activity.description}
                          </p>
                          <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                            {format(new Date(activity.createdAt), "h:mm a")}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Load more */}
        {data?.pagination && data.pagination.total > activities.length && (
          <div className="text-center">
            <Button variant="ghost" size="sm" className="text-xs">
              Show more
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
